import React from 'react'
import './SwipeView.scss'
import classNames from 'classnames'
import BackArrow from '../assets/ic_back_arrow.svg'
import Velocity from 'touch-velocity'
import windowSupportsTouchEvents from '../utils/windowSupportsTouchEvents'

const SwipeCell: React.FC<{ size: number, index: number, selectedIndex: number, onSelect: (index: number) => void, children: React.ReactChild }> = ({ size, index, selectedIndex, onSelect, children }) => {
  // const distanceToSelectedIndex = Math.abs(index - selectedIndex)
  // const diff = Math.min(distanceToSelectedIndex, 1) * 0.5

  return (
    <div
      className={classNames('SwipeCell', { selected: Math.round(selectedIndex) === index })}
      onClick={() => onSelect(index)}
      style={{
        transition: selectedIndex === Math.round(selectedIndex) ? 'transform 500ms cubic-bezier(0.000, 0.780, 0.375, 0.990)' : '0ms',
        // opacity: 1 - diff,
        transform: `translateX(${100 * (index - selectedIndex) - 50}%)`,// scale(${1 - diff })`,
        width: `${size}px`,
      }}
    >
      {/* <div className="child-container"> */}
      {children}
      {/* </div> */}
    </div>
  )
}

function useTouchVelocity(): [() => number, ((x: number) => void)] {
  const velocity = React.useRef<Velocity>()

  velocity.current = velocity.current ?? new Velocity()

  const dispatch = React.useCallback((x: number) => velocity.current?.updatePosition(x), [])

  const getter = React.useCallback(() => {
    return velocity.current?.getVelocity() ?? 0
  }, [])

  return [getter, dispatch]
}


const SwipeView: React.FC<{
  children: React.ReactChild[]
  itemHeight: number
  itemWidth: number
  onSelected: (index: number) => void
  onClickSelected: (index: number) => void
  selectedIndex: number
  setSelectedIndex: (value: number) => void
}> = ({
  itemHeight,
  itemWidth,
  children,
  onSelected,
  selectedIndex,
  setSelectedIndex,
  onClickSelected,
}) => {
  // const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [dragSelectedIndex, setDragSelectedIndex] = React.useState(selectedIndex)
  const [dragX, setDragX] = React.useState<number|null>(null)

  const [getVelocity, dispatchTouch] = useTouchVelocity()

  React.useEffect(() => {
    if (selectedIndex === Math.round(selectedIndex)) {
      onSelected(selectedIndex)
    }
  }, [selectedIndex, onSelected])

  React.useEffect(() => {
    if (dragX !== null) {
      const moveListener = (e: TouchEvent) => {
        setSelectedIndex(dragSelectedIndex + ((dragX - e.touches[0].clientX) / itemWidth))
        dispatchTouch(e.touches[0].clientX)
      }

      window.addEventListener('touchmove', moveListener)

      return () => {
        window.removeEventListener('touchmove', moveListener)
      }
    }
  }, [dragX, setSelectedIndex, dragSelectedIndex, dispatchTouch, itemWidth])

  React.useEffect(() => {
    const listener = () => {
      setDragX(null)
      setDragSelectedIndex(0)
      // dispatchTouch(null)
      const velocity = getVelocity()
      const negative = velocity < 0
      const absVelocity = Math.abs(velocity)

      // try to snap closer to the current position to prevent overshoot on small swipes
      const truncatedVelocity = Math.max(0, absVelocity - itemWidth * 7, absVelocity > (itemWidth / 2) ? itemWidth / 2 : 0)
      const finalVelocity = truncatedVelocity * (negative ? -1 : 1)

      setSelectedIndex(Math.min(Math.max(0, Math.round(selectedIndex - (finalVelocity / itemWidth))), children.length - 1))
    }

    window.addEventListener('blur', listener)
    window.addEventListener('touchcancel', listener)
    window.addEventListener('touchend', listener)

    return () => {
      window.removeEventListener('blur', listener)
      window.removeEventListener('touchcancel', listener)
      window.removeEventListener('touchend', listener)
    }
  }, [setDragX, setSelectedIndex, selectedIndex, children.length, dispatchTouch, getVelocity, itemWidth])

  return (
    <div
      className="SwipeView"
      style={{ height: `${itemHeight}px` }}
      onTouchStart={e => {
        setDragX(e.touches[0].clientX)
        setDragSelectedIndex(selectedIndex)
      }}
    >
      {
        children.map((child,i) => (
          <SwipeCell
            size={itemWidth}
            key={i}
            index={i}
            selectedIndex={selectedIndex}
            onSelect={it => {
              if (it === selectedIndex && selectedIndex === Math.round(selectedIndex)) {
                onClickSelected(it)
              } else {
                setSelectedIndex(it)
              }
            }}
          >
            {child}
          </SwipeCell>
        ))
      }
      {
        !windowSupportsTouchEvents && (
          <>
            <div
              className={classNames('navArrow left', { disabled: selectedIndex <= 0 })}
              style={{
                top: `${itemWidth / 2}px`,
                backgroundImage: `url(${BackArrow})`,
              }}
              onClick={() => setSelectedIndex(Math.round(selectedIndex - 1))}
            >
            </div>

            <div
              className={classNames('navArrow right', { disabled: selectedIndex >= children.length - 1 })}
              style={{
                top: `${itemWidth / 2}px`,
                backgroundImage: `url(${BackArrow})`,
              }}
              onClick={() => setSelectedIndex(Math.round(selectedIndex + 1))}
            >
            </div>
          </>
        )
      }
    </div>
  )
}

export default SwipeView
